import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, ArrowLeft, Eye, EyeOff, User, AtSign, Lock, Store } from 'lucide-react';
import { supabase } from '../../supabase';
import { APP_ROUTES } from '../../utils/auth';
import { hashPin } from '../../utils/hashPin';
import { getRouteForProfile, setStoredProfile } from '../../utils/session';
import AuthLayout, { ErrorBox, Spinner, authBtnStyle, SocialButtons } from './AuthLayout';

function isEmail(val) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim()); }

export default function Register() {
  const navigate = useNavigate();
  const [accountType, setAccountType] = useState('particulier'); // 'particulier' | 'merchant'
  const [name, setName]             = useState('');
  const [storeName, setStoreName]   = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  const isMerchant = accountType === 'merchant';

  const switchType = (type) => {
    if (type === accountType) return;
    setAccountType(type);
    setError('');
  };

  const handleRegister = async () => {
    const val = identifier.trim();
    if (!name.trim() || !val || password.length < 4) return;
    if (isMerchant && !storeName.trim()) return;
    setError(''); setLoading(true);

    const role = isMerchant ? 'merchant' : 'particulier';

    // Boutique : vérifier l'unicité du nom
    if (isMerchant) {
      const { data: existingStore } = await supabase.from('profiles')
        .select('id').ilike('store_name', storeName.trim()).eq('role', 'merchant').maybeSingle();
      if (existingStore) { setError('Ce nom de boutique est déjà utilisé.'); setLoading(false); return; }
    }

    const baseProfile = {
      name: name.trim(),
      role,
      account_status: 'approved',
      ...(isMerchant ? { store_name: storeName.trim() } : {}),
    };

    if (isEmail(val)) {
      const { data: existing } = await supabase.from('profiles')
        .select('id').eq('email', val.toLowerCase()).maybeSingle();
      if (existing) { setError('Cet email est déjà utilisé.'); setLoading(false); return; }

      const { error: authErr } = await supabase.auth.signUp({
        email: val.toLowerCase(),
        password,
        options: { data: { name: name.trim(), role, ...(isMerchant ? { store_name: storeName.trim() } : {}) } },
      });
      if (authErr) { setError(authErr.message || "Erreur lors de l'inscription."); setLoading(false); return; }

      const hashed = await hashPin(password);
      const { data: prof, error: profErr } = await supabase.from('profiles')
        .insert({ ...baseProfile, email: val.toLowerCase(), pin_hash: hashed })
        .select().single();
      if (profErr || !prof) { setError(isMerchant ? 'Erreur lors de la création de la boutique.' : 'Erreur lors de la création du profil.'); setLoading(false); return; }
      setStoredProfile(prof);
      navigate(getRouteForProfile(prof) || (isMerchant ? APP_ROUTES.merchant : APP_ROUTES.particulier), { replace: true });
    } else {
      const { data: existing } = await supabase.from('profiles')
        .select('id').eq('phone', val).eq('role', role).maybeSingle();
      if (existing) { setError('Ce numéro est déjà utilisé.'); setLoading(false); return; }

      const hashed = await hashPin(password);
      const { data: prof, error: profErr } = await supabase.from('profiles')
        .insert({ ...baseProfile, phone: val, pin_hash: hashed })
        .select().single();
      if (profErr || !prof) { setError(isMerchant ? 'Erreur lors de la création de la boutique.' : 'Erreur lors de la création du profil.'); setLoading(false); return; }
      setStoredProfile(prof);
      navigate(getRouteForProfile(prof) || (isMerchant ? APP_ROUTES.merchant : APP_ROUTES.particulier), { replace: true });
    }
    setLoading(false);
  };

  const handleOAuth = async (provider) => {
    setError('');
    const { error: e } = await supabase.auth.signInWithOAuth({
      provider, options: { redirectTo: `${window.location.origin}/login` },
    });
    if (e) setError(`Connexion ${provider === 'google' ? 'Google' : 'Apple'} indisponible pour le moment.`);
  };

  const ready = !loading && !!name.trim() && !!identifier.trim() && password.length >= 4
    && (!isMerchant || !!storeName.trim());

  const types = [
    { id: 'particulier', label: 'Particulier', Icon: User },
    { id: 'merchant',    label: 'E-commerçant', Icon: Store },
  ];

  return (
    <AuthLayout footer={isMerchant ? 'Wakaana Express · Créer une boutique' : 'Wakaana Express · Créer un compte'}>
      <button className="auth-back" onClick={() => navigate(APP_ROUTES.home)}>
        <ArrowLeft size={14} /> Retour à la connexion
      </button>

      <h2 className="auth-title">{isMerchant ? 'Créer ma boutique' : 'Créer un compte'}</h2>
      <p className="auth-subtitle">
        {isMerchant
          ? 'Déléguez votre logistique : stock, closing et livraison.'
          : 'Suivez vos livraisons en temps réel.'}
      </p>

      {/* Sélecteur de type de compte */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, background: '#f1f5f9', padding: 4, borderRadius: 13 }}>
        {types.map(({ id, label, Icon }) => {
          const active = accountType === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => switchType(id)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                padding: '11px 8px', borderRadius: 10, border: 'none', cursor: 'pointer',
                fontSize: 13.5, fontWeight: 700,
                background: active ? 'linear-gradient(135deg, #173b78, #2a66b8)' : 'transparent',
                color: active ? '#fff' : '#64748b',
                boxShadow: active ? '0 4px 14px rgba(23,59,120,0.28)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              <Icon size={16} /> {label}
            </button>
          );
        })}
      </div>

      {/* Nom (contact) */}
      <div className="auth-field">
        <span className="auth-fieldicon"><User size={17} /></span>
        <input
          className="auth-input with-icon"
          type="text" value={name}
          onChange={e => { setName(e.target.value); setError(''); }}
          placeholder={isMerchant ? 'Votre nom (responsable)' : 'Nom complet'}
          autoFocus
        />
      </div>

      {/* Nom de la boutique (marchand) */}
      {isMerchant && (
        <div className="auth-field">
          <span className="auth-fieldicon"><Store size={17} /></span>
          <input
            className={`auth-input with-icon${error ? ' err' : ''}`}
            type="text" value={storeName}
            onChange={e => { setStoreName(e.target.value); setError(''); }}
            placeholder="Nom de la boutique"
          />
        </div>
      )}

      {/* Identifiant */}
      <div className="auth-field">
        <span className="auth-fieldicon"><AtSign size={17} /></span>
        <input
          className={`auth-input with-icon${error ? ' err' : ''}`}
          type="text" value={identifier}
          onChange={e => { setIdentifier(e.target.value); setError(''); }}
          placeholder="Email ou téléphone"
        />
      </div>

      {/* Mot de passe */}
      <div className="auth-field">
        <span className="auth-fieldicon"><Lock size={17} /></span>
        <div style={{ position: 'relative' }}>
          <input
            className="auth-input with-icon with-eye"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleRegister()}
            placeholder="Mot de passe (4 caractères min.)"
          />
          <button className="auth-eye" onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      <div style={{ height: 6 }} />
      <ErrorBox error={error} />

      <button className="auth-btn" onClick={handleRegister} disabled={!ready} style={authBtnStyle(ready)}>
        {loading
          ? <Spinner size={18} light />
          : isMerchant
            ? <><Store size={17} /> Créer ma boutique</>
            : <><UserPlus size={17} /> Créer mon compte</>}
      </button>

      <div className="auth-divider"><span>Déjà un compte ?</span></div>

      <button className="auth-secondary" onClick={() => navigate(APP_ROUTES.home)}>
        Se connecter
      </button>

      {/* Connexion sociale — uniquement pour les particuliers */}
      {!isMerchant && (
        <>
          <div style={{ height: 12 }} />
          <SocialButtons
            onApple={() => handleOAuth('apple')}
            onGoogle={() => handleOAuth('google')}
          />
        </>
      )}
    </AuthLayout>
  );
}
